/**
 * WebSmStart — the spy intro (ported verbatim from Glow's WebSmStart_type.jsx).
 * A gold key spins center-screen and flies to the corner, then a CLASSIFIED
 * card fades in with title/subtitle/question and two choice buttons.
 *
 * Options: Title / Subtitle / Message / Brand / LabelForCLICK1 / LabelForCLICK2
 *   / LoginLabel / AccentColor / TitleColor / MessageColor
 *   (BgColor / KeyColor are accepted but unused: the key is a clip on black)
 *   / ButtonTextColor
 * Emits CLICK1 (and CLICK2 when the second button is shown).
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import {
  CHOICE_BTN_CLASS,
  FLY_IN_CLASS,
  POP_IN_CLASS,
  SECTION_CLASS,
  stepVars,
} from "./step-shell";
import type { StepTypeProps } from "./types";
import keySpinVideo from "./key-spin.mp4";
import keySpinPoster from "./key-spin-poster.jpg";

const KEY_RATE = 1.6;
/** The key rests on its QR face (the clip's first frame) this long first. */
const HOLD_MS = 2000;

/** Keep a line of text on one line: its font shrinks just enough to fit its
 *  box (a long name in the title would otherwise wrap), never grows. */
function useFitOneLine(text: string) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      el.style.fontSize = "";
      if (el.scrollWidth > el.clientWidth)
        el.style.fontSize = `${(parseFloat(getComputedStyle(el).fontSize) * el.clientWidth) / el.scrollWidth}px`;
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text]);
  return ref;
}

export default function WebSmStartType({ getOpt, dispatch }: StepTypeProps) {
  const accent = getOpt("AccentColor") || "#d6a94e";
  const titleColor = getOpt("TitleColor") || "#f4eddb";
  const messageColor = getOpt("MessageColor") || "#e7ddca";
  const btnText = getOpt("ButtonTextColor") || "#e6d3a4";

  const brand = getOpt("Brand");
  const title = getOpt("Title") || "Ashley gave you a one-use key.";
  const titleRef = useFitOneLine(title);
  const subtitle =
    getOpt("Subtitle") || "Let's see if they were right about you.";
  const message =
    getOpt("Message") ||
    "Have you ever had a feeling, deep in your soul, that you were meant to be doing something far more important - something that matters a lot more than what you have been doing?";
  const label1 = getOpt("LabelForCLICK1") || "Yes, I get that feeling a lot";
  const label2 = getOpt("LabelForCLICK2") || "";

  // The key spins (the clip is one 2.8s turn, played at KEY_RATE), flies to
  // the top-right corner after about one turn, and keeps spinning there. The
  // clip has no alpha — it is rendered on pure black — so this screen's
  // backdrop is pure black too (no accent wash, BgColor ignored) and the clip
  // simply merges.
  //
  // The source turn is eased, so a loop restarting on its slowest frames read
  // as a stall; the bundled clip is trimmed past that ease-in (ffmpeg -ss 0.3)
  // so the native loop is seamless. No seeking: the dev server hands the file
  // through the MSW worker without range support, so seeks clamp to 0 — which
  // is why the clip itself is cut to open on the key's QR face, where it rests
  // for HOLD_MS before it starts to turn.
  // while the key rests on its QR face, the code glows to draw the eye
  const [qrLit, setQrLit] = useState(true);
  const keyRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = keyRef.current;
    if (!v) return;
    v.playbackRate = KEY_RATE;
    // iOS will not autoplay unless the element is muted as a PROPERTY — React
    // only sets the attribute, which Safari ignores — and it wants an explicit
    // play() rather than trusting `autoPlay`. Without both the key sits on its
    // first frame while the CSS flight still runs, so it reads as a still
    // image sliding to the corner. `play()` is also retried once the data is
    // there, for the case where the first call lands before the clip is ready.
    v.muted = true;
    v.defaultMuted = true;
    let held = true;
    const start = () => {
      if (held) return;
      v.playbackRate = KEY_RATE;
      void v.play().catch(() => {});
    };
    const hold = setTimeout(() => {
      held = false;
      setQrLit(false);
      start();
    }, HOLD_MS);
    v.addEventListener("loadeddata", start);
    v.addEventListener("canplay", start);

    const onTouch = () => start();
    document.addEventListener("touchstart", onTouch, {
      once: true,
      passive: true,
    });

    return () => {
      clearTimeout(hold);
      v.removeEventListener("loadeddata", start);
      v.removeEventListener("canplay", start);
      document.removeEventListener("touchstart", onTouch);
    };
  }, []);
  const [flew, setFlew] = useState(false);
  // Once the key has landed (its flight, then the copy fading in), it is
  // handed to a twin inside the copy block, in the room above the title — so
  // it sits with the title on every screen, whatever the layout does late.
  const [landed, setLanded] = useState(false);
  useEffect(() => {
    if (!flew) return;
    const t = setTimeout(() => setLanded(true), 1450);
    return () => clearTimeout(t);
  }, [flew]);

  // The copy is centred on the screen like the next steps, with room kept at
  // its top for the key: the key parks in that room, so the key and the copy
  // sit together in the middle at any screen height. Measured, because the
  // copy's height depends on the authored text.
  const copyRef = useRef<HTMLDivElement>(null);
  const [parkTop, setParkTop] = useState<number | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      const el = copyRef.current;
      if (!el) return;
      // the key's middle, in the middle of the room kept for it; parked, the
      // key is ~14dvh (19.5dvh of frame x the 73% it fills) in a 20dvh room
      // (its real top padding, in px — dvh and innerHeight disagree on iOS)
      const room = parseFloat(getComputedStyle(el).paddingTop) || 0;
      setParkTop(el.offsetTop + room * 0.5);
    };
    measure();
    // Re-measure whenever the copy's size changes (web fonts arriving, the
    // authored text rendering) — the centred block moves, and the key must
    // follow it or it lands on the title.
    const ro = new ResizeObserver(measure);
    if (copyRef.current) ro.observe(copyRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);
  useEffect(() => {
    const t = setTimeout(
      () => setFlew(true),
      HOLD_MS + (2800 / KEY_RATE) * 0.9,
    );
    return () => clearTimeout(t);
  }, []);

  // Reveal sequence (after the key flies off): the two sentences land with the
  // card, then the question card flies in from the right, then the button pops.
  const [showQuestion, setShowQuestion] = useState(false);
  const [showButton, setShowButton] = useState(false);
  useEffect(() => {
    if (!flew) return;
    // a beat for the key to finish flying and the lines to land
    const t = setTimeout(() => setShowQuestion(true), 1700);
    return () => clearTimeout(t);
  }, [flew]);
  useEffect(() => {
    if (!showQuestion) return;
    const t = setTimeout(() => setShowButton(true), 700);
    return () => clearTimeout(t);
  }, [showQuestion]);

  // TitleColor / MessageColor are authored too, so they ride along as custom
  // properties. The backdrop is pure black to match the key clip (see above).
  const vars = {
    ...stepVars({ accent, bg: "#000", btnText }),
    "--sm-brand-glow": `${accent}59`,
    "--sm-title": titleColor,
    "--sm-message": messageColor,
  } as CSSProperties;

  return (
    <section style={vars} className={`${SECTION_CLASS} bg-none px-4 py-[70px]`}>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,transparent_54%,rgba(0,0,0,.58))]" />

      {brand ? (
        <span className="absolute top-[15px] left-[22px] z-[3] font-stencil text-[13px] tracking-[3px] text-[var(--sm-accent)] [text-shadow:0_0_12px_var(--sm-brand-glow)]">
          {brand}
        </span>
      ) : null}

      {/* no Login here: returning operatives sign in on the entry screens
          (no key: "Sign in with my number"), which take them to HQ */}

      <div
        // `transition-all`, not a property list: the key moves via `top` and
        // via Tailwind's standalone translate/scale/rotate, and all four share
        // the same 1s curve.
        style={flew && parkTop != null ? { top: parkTop } : undefined}
        // screen-blended: the clip's black frame vanishes into the page
        className={`pointer-events-none absolute left-1/2 z-[4] mix-blend-screen transition-all duration-1000 ease-[cubic-bezier(.5,0,.3,1)] ${landed ? "hidden" : ""} ${
          flew
            ? `${parkTop == null ? "top-[10%]" : ""} -translate-x-1/2 -translate-y-1/2 scale-[.195] rotate-[14deg]`
            : "top-1/2 -translate-x-1/2 -translate-y-1/2 scale-[.9]"
        }`}
      >
        {/* as big as the page: fill the full height (key is centred, sides crop) */}
        <video
          ref={keyRef}
          src={keySpinVideo}
          // the QR face it rests on (HOLD_MS) — iOS shows nothing of a clip it
          // has not started playing, so the poster is what holds the frame
          poster={keySpinPoster}
          muted
          loop
          playsInline
          preload="auto"
          // clipped to the key itself, so the opaque black frame around it
          // doesn't cover anything once it is parked in the corner
          // max-w-none: the default max-width capped this frame at half the
          // screen, shrinking the key inside it, so it landed smaller than its
          // twin and jumped when the twin took over
          className="block h-[100dvh] w-auto max-w-none [clip-path:inset(13.5%_27%_13.5%_27%)]"
        />
        {/* the QR on its face: two lines of light start at opposite corners
            (top left, bottom right) and draw the square's sides together,
            meeting at the other two, while the key rests there */}
        {qrLit && !flew ? (
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="pointer-events-none absolute top-[24.7%] left-[39.5%] h-[10.8%] w-[18.7%] overflow-visible [filter:drop-shadow(0_0_4px_#ffd27a)_drop-shadow(0_0_14px_#f5b94a)]"
          >
            {["M0 0 H100 V100", "M100 100 H0 V0"].map((d) => (
              <path
                key={d}
                d={d}
                fill="none"
                stroke="#fff1c8"
                strokeWidth={4}
                strokeLinecap="square"
                className="animate-[smQrDraw_1.5s_cubic-bezier(.45,0,.55,1)_.2s_both] [stroke-dasharray:200]"
              />
            ))}
          </svg>
        ) : null}
      </div>

      <div
        // the copy waits for the key to finish its 1s flight; centred, with
        // the key's room (its top padding) kept at its top
        ref={copyRef}
        className={`relative z-[3] mx-auto w-full max-w-[432px] px-[26px] pt-[20dvh] pb-[26px] text-center transition-opacity duration-[400ms] delay-1000 ${
          flew ? "opacity-100" : "opacity-0"
        }`}
      >
        {/* the landed key: the flying key's twin, in the room kept above the
            title (the block's top padding) — same framing as the parked key */}
        <div
          aria-hidden
          className={`pointer-events-none absolute top-[10dvh] left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[14deg] mix-blend-screen ${
            landed ? "" : "invisible"
          }`}
        >
          <video
            src={keySpinVideo}
            poster={keySpinPoster}
            muted
            loop
            autoPlay
            playsInline
            className="block h-[19.5dvh] w-auto [clip-path:inset(13.5%_27%_13.5%_27%)]"
          />
        </div>
        <div
          ref={titleRef}
          className="-mx-[26px] text-[clamp(15px,5.4cqw,24px)] leading-[1.25] font-bold tracking-[-0.2px] whitespace-nowrap text-[var(--sm-title)]"
        >
          {title}
        </div>
        {subtitle ? (
          <div className="mt-[14px] mb-[22px] text-[clamp(12.5px,4.3cqw,19px)] leading-[1.35] text-[#a99d86] italic">
            {subtitle}
          </div>
        ) : null}

        {/* the question — flies in on a card from the right */}
        {message ? (
          <div
            className={`rounded-xl border border-[var(--sm-accent-edge)] bg-[rgb(18_16_18/0.92)] px-[22px] py-5 text-[clamp(18px,4.8cqw,23px)] leading-[1.5] font-normal text-pretty text-[var(--sm-message)] shadow-[0_20px_60px_rgba(0,0,0,.55)] ${FLY_IN_CLASS} ${
              showQuestion
                ? "translate-x-0 opacity-100"
                : "translate-x-[120%] opacity-0"
            }`}
          >
            {message}
          </div>
        ) : null}

        {/* button — pops in after the card */}
        <div
          className={`mt-6 flex flex-col items-stretch gap-[11px] sm:flex-row sm:flex-wrap sm:justify-center ${POP_IN_CLASS} ${
            showButton
              ? "scale-100 opacity-100"
              : "pointer-events-none scale-[.8] opacity-0"
          }`}
        >
          <button
            type="button"
            className={CHOICE_BTN_CLASS}
            onClick={() => {
              dispatch("CLICK1");
            }}
          >
            {label1}
          </button>
          {label2 ? (
            <button
              type="button"
              className={CHOICE_BTN_CLASS}
              onClick={() => dispatch("CLICK2")}
            >
              {label2}
            </button>
          ) : null}
        </div>
      </div>
      <style>{`@keyframes smQrDraw{0%{stroke-dashoffset:200;opacity:1}70%{stroke-dashoffset:0;opacity:1}100%{stroke-dashoffset:0;opacity:0}}`}</style>
    </section>
  );
}
